import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'

import { duration, easing } from '@twistopayments/design-system/styles'

export type Colors = keyof ReturnType<typeof colors>
export type Sizes = keyof ReturnType<typeof sizes>

export const iconButton = (theme: Theme) => css`
  border: none;
  padding: 0;
  margin: 0;
  min-height: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  aspect-ratio: 1 / 1;
  line-height: 0;
  border-radius: ${theme.borderRadius.circle};
  transition: background ${duration.short}ms ${easing.easeIn};
  flex-shrink: 0;

  &[disabled] {
    color: ${theme.palette.disabled.accent};
    background: ${theme.palette.disabled.main};
    pointer-events: none;
  }
`

const colorPseudoClasses = ({
  hoverBackgroundColor,
  focusOutlineColor,
}: {
  hoverBackgroundColor: string
  focusOutlineColor: string
}) => css`
  &:hover,
  &:focus {
    background: ${hoverBackgroundColor};
    transition: background ${duration.short}ms ${easing.easeOut};
  }

  &:focus-visible {
    outline: 2px solid ${focusOutlineColor};
    outline-offset: 2px;
    transition: background ${duration.short}ms ${easing.easeOut};
  }
`

export const colors = (theme: Theme) => ({
  primary: css`
    color: ${theme.palette.primary.reversedContrast};
    background: ${theme.palette.primary.mainContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.primary.maxContrast,
      focusOutlineColor: theme.palette.primary.maxContrast,
    })}
  `,
  secondary: css`
    color: ${theme.palette.primary.mainContrast};
    background: ${theme.palette.primary.lowContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.primary.mediumContrast,
      focusOutlineColor: theme.palette.primary.mediumContrast,
    })}
  `,
  // color/error/low-contrast
  negative: css`
    color: ${theme.palette.error.main};
    background: ${theme.palette.error.lowContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.error.mediumContrast,
      focusOutlineColor: theme.palette.error.mediumContrast,
    })}
  `,
  warning: css`
    color: ${theme.palette.warning.main};
    background: ${theme.palette.warning.lowContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.warning.mediumContrast,
      focusOutlineColor: theme.palette.warning.mediumContrast,
    })}
  `,
  neutral: css`
    color: ${theme.palette.ghost.main};
    background: ${theme.palette.ghost.lowContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.ghost.lowContrast,
      focusOutlineColor: theme.palette.ghost.lowContrast,
    })}
  `,
  ghost: css`
    color: ${theme.palette.ghost.main};
    background: transparent;

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.ghost.lowContrast,
      focusOutlineColor: theme.palette.ghost.lowContrast,
    })}
  `,
  transparent: css`
    color: inherit;
    background: transparent;

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.ghost.lowContrast,
      focusOutlineColor: theme.palette.ghost.lowContrast,
    })}
  `,
})

export const sizes = (theme: Theme) => ({
  small: css`
    // size 6 is not defined in our design system
    padding: calc(${theme.spacing[4]} * 1.5);
  `,
  medium: css`
    padding: ${theme.spacing[8]};
  `,
})
